import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  MoreButton,
} from '@makeably/creativex-design-system';
import ConfirmationModal from 'components/molecules/ConfirmationModal';
import {
  addErrorToast,
  addToast,
} from 'components/organisms/Toasts';
import {
  destroy,
  post,
} from 'utilities/requests';
import {
  duplicateInternalGuidelinePath,
  editInternalGuidelinePath,
  internalGuidelinePath,
  internalGuidelinesPath,
  updateStateInternalGuidelinePath,
} from 'utilities/routes';

const guidelineProps = PropTypes.shape({
  id: PropTypes.number.isRequired,
  isSubscribed: PropTypes.bool.isRequired,
  state: PropTypes.string.isRequired,
});

const propTypes = {
  definitionCount: PropTypes.number.isRequired,
  guideline: guidelineProps.isRequired,
  showAsButtons: PropTypes.bool,
  usageCount: PropTypes.number,
};

const defaultProps = {
  showAsButtons: false,
  usageCount: 1,
};

function renderControls(options, showAsButtons) {
  if (showAsButtons) {
    return (
      <div className="u-flexRow u-gap-8">
        { options.map(({
          disabled,
          label,
          onClick,
          url,
          variant,
        }) => (
          <Button
            key={label}
            disabled={disabled}
            label={label}
            url={url}
            variant={variant}
            onClick={onClick}
          />
        )) }
      </div>
    );
  }

  return <MoreButton options={options} />;
}

function GuidelineActions({
  definitionCount,
  guideline,
  showAsButtons,
  usageCount,
}) {
  const isStateDisabled = guideline.isSubscribed || guideline.state !== 'editing' || definitionCount === 0;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [isActive, setIsActive] = useState(false);

  const handleDelete = async () => {
    setIsActive(true);

    const { isError } = await destroy(internalGuidelinePath(guideline.id));

    if (isError) {
      addErrorToast('Could not delete guideline');
    } else {
      addToast('Guidline sucessfully deleted');
      window.location.href = internalGuidelinesPath();
    }

    setConfirmDelete(false);
    setIsActive(false);
  };

  const handleDuplicate = async () => {
    setIsActive(true);

    const { isError } = await post(duplicateInternalGuidelinePath(guideline.id));

    if (isError) {
      addErrorToast('Could not duplicate guideline');
    } else {
      addToast('Guidline sucessfully duplicated');
      window.location.href = internalGuidelinesPath();
    }

    setIsActive(false);
  };

  const handleStateChange = async (state) => {
    setIsActive(true);

    const formData = new FormData();
    formData.append('state', state);

    const { isError } = await post(updateStateInternalGuidelinePath(guideline.id), formData);

    if (isError) {
      addErrorToast('Could not update guideline state');
    } else {
      addToast('Guidline state sucessfully updated');
      window.location.reload();
    }

    setConfirmState(null);
    setIsActive(false);
  };

  const options = [
    {
      disabled: isStateDisabled,
      label: 'Activate',
      onClick: () => setConfirmState('active'),
      variant: 'secondary',
    },
    {
      disabled: guideline.isSubscribed || usageCount !== 0,
      label: 'Delete',
      onClick: () => setConfirmDelete(true),
      variant: 'destructive',
    },
    {
      label: 'Duplicate',
      onClick: handleDuplicate,
      variant: 'secondary',
    },
    {
      disabled: guideline.isSubscribed,
      label: 'Edit',
      url: editInternalGuidelinePath(guideline.id),
      variant: 'secondary',
    },
    {
      disabled: isStateDisabled,
      label: 'Trial',
      onClick: () => setConfirmState('trial'),
      variant: 'secondary',
    },
  ];

  return (
    <>
      { renderControls(options, showAsButtons) }
      <ConfirmationModal
        actionButtonActive={isActive}
        isOpen={confirmDelete}
        message="Are you sure you want to delete this guideline?"
        title="Confirm Guideline Delete"
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
      <ConfirmationModal
        actionButtonActive={isActive}
        isOpen={Boolean(confirmState)}
        message={`Are you sure you want change the state of this guideline to '${confirmState}'?`}
        title="Confirm Guideline State Change"
        onClose={() => setConfirmState(null)}
        onConfirm={() => handleStateChange(confirmState)}
      />
    </>
  );
}

GuidelineActions.propTypes = propTypes;
GuidelineActions.defaultProps = defaultProps;

export default GuidelineActions;
